/**
 * Block selections. The UI produces contiguous runs of the visible outline (⇧↑/↓, ⇧-click, drag).
 * Operations act on the selection's *roots* (selected nodes without a selected ancestor), each of
 * which carries its whole subtree. Roots are grouped into runs of consecutive siblings.
 *
 * In a contiguous run, groups get shallower as you go down (the run leaves subtrees), and only the
 * last group can have unselected siblings after it. Structural ops rely on both facts.
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { isAncestor, visibleRows } from './tree.ts'

export interface Groups {
  /** Selected roots in document order. */
  readonly roots: NodeId[]
  /** Roots split into runs of consecutive live siblings. */
  readonly groups: NodeId[][]
}

const NONE: Groups = { roots: [], groups: [] }

/**
 * Normalise a selection inside view `root`. Returns NONE if any id is not live, not inside the
 * view, hidden, or if the ids are not one contiguous run of visible rows.
 */
export function selectionGroups(g: Graph, root: NodeId, ids: readonly NodeId[]): Groups {
  if (ids.length === 0) return NONE
  for (const id of ids) {
    if (id === root || !g.isLive(id) || !isAncestor(g, root, id)) return NONE
  }
  let ordered: NodeId[]
  if (ids.length === 1) {
    ordered = [ids[0]!]
  } else {
    const rows = visibleRows(g, root)
    const pos = new Map<NodeId, number>()
    rows.forEach((r, i) => pos.set(r.id, i))
    const idx: number[] = []
    for (const id of new Set(ids)) {
      const p = pos.get(id)
      if (p === undefined) return NONE
      idx.push(p)
    }
    idx.sort((a, b) => a - b)
    for (let i = 1; i < idx.length; i++) if (idx[i] !== idx[i - 1]! + 1) return NONE
    ordered = idx.map((i) => rows[i]!.id)
  }
  const set = new Set(ordered)
  const roots = ordered.filter((id) => {
    for (let cur = g.must(id).parentId; cur !== null && cur !== root; cur = g.must(cur).parentId) {
      if (set.has(cur)) return false
    }
    return true
  })
  const groups: NodeId[][] = []
  for (const id of roots) {
    const last = groups[groups.length - 1]
    if (last && g.must(last[0]!).parentId === g.must(id).parentId) last.push(id)
    else groups.push([id])
  }
  return { roots, groups }
}
